import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Download, QrCode, ArrowLeft, Smartphone, Crown, Shield, Star, Receipt, Calendar, FileText, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PatientCard = () => {
  const [user, setUser] = useState(null);
  const [card, setCard] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [transactionStats, setTransactionStats] = useState({ total: 0, count: 0 });
  const cardRef = useRef(null);
  const navigate = useNavigate();

  // Available years for filtering
  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear, currentYear - 1, currentYear - 2];

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/auth');
      return;
    }
    setUser(JSON.parse(userData));
    loadCard();
    loadSubscription();
  }, [navigate]);

  // Load transactions when section is opened or year changes
  useEffect(() => {
    if (showTransactions) {
      loadTransactions();
    }
  }, [showTransactions, selectedYear]);

  const loadCard = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/my-card`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCard(data);
        // Se il backend restituisce il nome del titolare, aggiorna l'utente locale
        if (data.holder_name) {
          const [firstName, ...lastNameParts] = data.holder_name.split(' ');
          setUser(prev => ({
            ...prev,
            card_holder_name: data.holder_name,
            is_family_member: data.is_family_member,
            first_name: firstName || prev?.first_name,
            last_name: lastNameParts.join(' ') || prev?.last_name
          }));
        }
      }
    } catch (error) {
      console.error('Error loading card:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadTransactions = async () => {
    const token = localStorage.getItem('token');
    setTransactionsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/my-transactions?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setTransactionStats({ total: data.total_amount || 0, count: data.count || 0 });
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Errore nel caricamento delle transazioni');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const downloadTransactionPDF = async (transactionId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/transaction/${transactionId}/pdf?token=${token}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ricevuta_${transactionId.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Ricevuta scaricata');
      } else {
        toast.error('Errore nel download della ricevuta');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Errore nel download');
    }
  };

  const downloadAnnualSummary = async () => {
    const token = localStorage.getItem('token');
    try {
      toast.info('Generazione riepilogo in corso...');
      const response = await fetch(`${BACKEND_URL}/api/payments/annual-summary/${selectedYear}/pdf?token=${token}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `riepilogo_spese_${selectedYear}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`Riepilogo ${selectedYear} scaricato`);
      } else {
        toast.error('Errore nella generazione del riepilogo');
      }
    } catch (error) {
      console.error('Error downloading annual summary:', error);
      toast.error('Errore nel download');
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null
      });
      
      const link = document.createElement('a');
      link.download = `tessera-primevision-${card?.card_number || 'card'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading card:', error);
    }
  };

  const addToWallet = async () => {
    if (!cardRef.current) return;
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: null
      });
      
      const imageUrl = canvas.toDataURL('image/png');
      
      if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        const blob = await (await fetch(imageUrl)).blob();
        const file = new File([blob], `tessera-primevision-${card?.card_number || 'card'}.png`, { type: 'image/png' });
        
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: 'Tessera PRIME VISION',
            text: 'La mia tessera PRIME VISION per il check-in rapido',
            files: [file]
          });
        } else {
          const link = document.createElement('a');
          link.download = `tessera-wallet-primevision-${card?.card_number || 'card'}.png`;
          link.href = imageUrl;
          link.click();
        }
      } else {
        alert('Per aggiungere la tessera al wallet:\n\n1. Scarica l\'immagine della tessera\n2. Aprila sul tuo smartphone\n3. Salvala nella galleria\n4. Da iPhone: usa l\'app Wallet o Shortcuts\n5. Da Android: usa Google Wallet o screenshot nella galleria');
        
        const link = document.createElement('a');
        link.download = `tessera-wallet-primevision-${card?.card_number || 'card'}.png`;
        link.href = imageUrl;
        link.click();
      }
    } catch (error) {
      console.error('Error adding to wallet:', error);
      alert('Per aggiungere al wallet, scarica l\'immagine e salvala sul tuo telefono.');
    }
  };

  // Get subscription tier info for card design
  const getCardDesign = () => {
    if (!subscription || subscription.status !== 'active') {
      // No subscription - Standard Patient Card
      return {
        type: 'STANDARD',
        label: 'PAZIENTE',
        gradient: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 40%, #1B6B8A 100%)',
        accentColor: '#5EEAD4',
        badgeGradient: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)',
        badgeBorder: '#5EEAD4',
        icon: CreditCard,
        decorativePattern: 'teal'
      };
    }
    
    const planName = subscription.plan_name?.toLowerCase();
    
    if (planName === 'premium') {
      // Premium - Elegant Dark Blue with Gold
      return {
        type: 'PREMIUM',
        label: 'PREMIUM',
        gradient: 'linear-gradient(135deg, #1E293B 0%, #0F172A 40%, #020617 100%)',
        accentColor: '#F59E0B',
        badgeGradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
        badgeBorder: '#FBBF24',
        icon: Crown,
        decorativePattern: 'gold'
      };
    } else if (planName === 'base') {
      // Base - Silver/Grey elegant
      return {
        type: 'BASE',
        label: 'BASE',
        gradient: 'linear-gradient(135deg, #374151 0%, #1F2937 40%, #111827 100%)',
        accentColor: '#9CA3AF',
        badgeGradient: 'linear-gradient(135deg, #9CA3AF, #6B7280)',
        badgeBorder: '#D1D5DB',
        icon: Shield,
        decorativePattern: 'silver'
      };
    }
    
    return {
      type: 'STANDARD',
      label: 'PAZIENTE',
      gradient: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 40%, #1B6B8A 100%)',
      accentColor: '#5EEAD4',
      badgeGradient: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)',
      badgeBorder: '#5EEAD4',
      icon: CreditCard,
      decorativePattern: 'teal'
    };
  };

  const design = getCardDesign();
  const IconComponent = design.icon;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: '#2A9FCB' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: 'linear-gradient(180deg, #e8f4fd 0%, #F9FAFB 100%)' }}>
      <div className="max-w-lg mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard/patient')}
          className="flex items-center gap-2 mb-8 hover:opacity-70 transition-opacity"
          style={{ color: '#2A9FCB' }}
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Torna alla Dashboard</span>
        </button>

        <h1 className="text-3xl font-bold mb-2" style={{ color: '#2A9FCB', fontFamily: 'Lora, serif' }}>
          La Tua Tessera
        </h1>
        <p className="text-gray-600 mb-8">Mostra il QR code all&apos;accettazione per il check-in rapido</p>

        {/* Card Preview */}
        <div 
          ref={cardRef}
          className="relative rounded-2xl overflow-hidden shadow-2xl mb-8"
          style={{ 
            aspectRatio: '1.586',
            background: design.gradient
          }}
        >
          {/* Decorative Pattern - Premium Gold */}
          {design.decorativePattern === 'gold' && (
            <>
              <div className="absolute top-0 right-0 w-72 h-72 opacity-10"
                   style={{ 
                     background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)',
                     transform: 'translate(30%, -30%)'
                   }}></div>
              <div className="absolute bottom-0 left-0 w-56 h-56 opacity-10"
                   style={{ 
                     background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)',
                     transform: 'translate(-30%, 30%)'
                   }}></div>
              {/* Gold line accent */}
              <div className="absolute top-0 left-0 right-0 h-1"
                   style={{ background: 'linear-gradient(90deg, transparent, #F59E0B, transparent)' }}></div>
            </>
          )}
          
          {/* Decorative Pattern - Silver/Base */}
          {design.decorativePattern === 'silver' && (
            <>
              <div className="absolute top-0 right-0 w-64 h-64 opacity-10"
                   style={{ 
                     background: 'radial-gradient(circle, #9CA3AF 0%, transparent 70%)',
                     transform: 'translate(30%, -30%)'
                   }}></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 opacity-10"
                   style={{ 
                     background: 'radial-gradient(circle, #9CA3AF 0%, transparent 70%)',
                     transform: 'translate(-30%, 30%)'
                   }}></div>
              {/* Silver line accent */}
              <div className="absolute top-0 left-0 right-0 h-1"
                   style={{ background: 'linear-gradient(90deg, transparent, #9CA3AF, transparent)' }}></div>
            </>
          )}
          
          {/* Decorative Pattern - Teal Standard */}
          {design.decorativePattern === 'teal' && (
            <>
              <div className="absolute top-0 right-0 w-64 h-64 opacity-20"
                   style={{ 
                     background: 'radial-gradient(circle, #5EEAD4 0%, transparent 70%)',
                     transform: 'translate(30%, -30%)'
                   }}></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 opacity-20"
                   style={{ 
                     background: 'radial-gradient(circle, #5EEAD4 0%, transparent 70%)',
                     transform: 'translate(-30%, 30%)'
                   }}></div>
              {/* Teal line accent */}
              <div className="absolute top-0 left-0 right-0 h-1"
                   style={{ background: 'linear-gradient(90deg, transparent, #5EEAD4, transparent)' }}></div>
            </>
          )}

          {/* Card Content */}
          <div className="absolute inset-0 p-6 flex flex-col justify-between">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div>
                <img 
                  src="/logo-primevision-full.png" 
                  alt="PRIME VISION" 
                  className="h-6 w-auto object-contain brightness-0 invert"
                />
                <p className="text-[10px] tracking-widest mt-1" style={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.2em' }}>
                  CENTRO OFTALMOLOGICO
                </p>
              </div>
              
              {/* Subscription Badge */}
              <div 
                className="px-4 py-2 rounded-full flex items-center gap-2 shadow-lg"
                style={{ 
                  background: design.badgeGradient,
                  border: `1px solid ${design.badgeBorder}40`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}
              >
                <IconComponent className="w-4 h-4" style={{ color: design.type === 'PREMIUM' ? '#1E293B' : '#FFFFFF' }} />
                <span className="text-xs font-bold tracking-wider" 
                      style={{ color: design.type === 'PREMIUM' ? '#1E293B' : '#FFFFFF' }}>
                  {design.label}
                </span>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex justify-center">
              {card?.qr_code_image ? (
                <div className="bg-white p-3 rounded-xl shadow-lg"
                     style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                  <img 
                    src={card.qr_code_image} 
                    alt="QR Code" 
                    className="w-28 h-28"
                  />
                </div>
              ) : (
                <div className="bg-white/20 p-6 rounded-xl">
                  <QrCode className="w-20 h-20" style={{ color: 'rgba(255,255,255,0.5)' }} />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-end">
              <div>
                <p className="text-[10px] mb-1 tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {card?.is_family_member ? 'FAMILIARE DI' : 'TITOLARE'}
                </p>
                <p className="text-lg font-semibold tracking-wide" style={{ color: '#FFFFFF', fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {card?.holder_name?.toUpperCase() || `${user?.first_name?.toUpperCase() || ''} ${user?.last_name?.toUpperCase() || ''}`.trim() || 'NOME COGNOME'}
                </p>
                {card?.is_family_member && card?.member_name && (
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Membro: {card.member_name}
                  </p>
                )}
                <p className="text-[9px] mt-1 tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  DAL {card?.created_at ? new Date(card.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : card?.issued_at ? new Date(card.issued_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : user?.created_at ? new Date(user.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '---'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] mb-1 tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>N° TESSERA</p>
                <p className="text-base font-mono tracking-wider font-bold" style={{ color: design.accentColor, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  {card?.card_number || 'PV-00000000'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <button
            onClick={downloadCard}
            className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
            style={{ background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)' }}
            data-testid="download-card-btn"
          >
            <Download className="w-5 h-5" />
            Scarica Tessera (PNG)
          </button>

          <button
            onClick={addToWallet}
            className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
            style={{ background: '#111827', color: '#FFFFFF' }}
            data-testid="add-to-wallet-btn"
          >
            <Smartphone className="w-5 h-5" />
            Aggiungi al Wallet
          </button>

          {/* Info Box */}
          <div className="p-5 rounded-xl" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)', border: '1px solid #99F6E4' }}>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg" style={{ background: '#CCFBF1' }}>
                <QrCode className="w-5 h-5" style={{ color: '#2A9FCB' }} />
              </div>
              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#2A9FCB' }}>Come usare la tessera</h3>
                <ul className="text-sm text-gray-600 space-y-1.5">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#2A9FCB' }}></div>
                    <strong>Scannerizza il QR code</strong> o inserisci il numero di tessera al check-in
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#2A9FCB' }}></div>
                    L&apos;operatore vedrà <strong>quanto devi pagare</strong> per la prescrizione
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#2A9FCB' }}></div>
                    Se abbonato: prestazioni incluse o scontate
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#2A9FCB' }}></div>
                    Non abbonato: pagamento tariffa piena in sede
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card Details */}
          <div className="p-5 rounded-xl bg-white shadow-sm" style={{ border: '1px solid #E5E7EB' }}>
            <h3 className="font-semibold mb-4" style={{ color: '#2A9FCB' }}>Dettagli Tessera</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Numero Tessera</span>
                <span className="font-mono font-medium" style={{ color: '#2A9FCB' }}>{card?.card_number || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Tipo Abbonamento</span>
                <span className="px-2 py-1 rounded-full text-xs font-semibold"
                      style={{ 
                        background: design.type === 'PREMIUM' ? '#FEF3C7' : design.type === 'BASE' ? '#F3F4F6' : '#CCFBF1',
                        color: design.type === 'PREMIUM' ? '#92400E' : design.type === 'BASE' ? '#374151' : '#2A9FCB'
                      }}>
                  {design.label}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Emessa il</span>
                <span className="font-medium">{card?.issued_at ? new Date(card.issued_at).toLocaleDateString('it-IT') : '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Stato</span>
                <span className="px-2 py-1 rounded-full text-xs font-medium" 
                      style={{ background: '#D1FAE5', color: '#065F46' }}>
                  {card?.is_active ? 'Attiva' : 'Non attiva'}
                </span>
              </div>
            </div>
          </div>

          {/* Storico Pagamenti */}
          <div className="rounded-xl bg-white shadow-sm overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
            <button
              onClick={() => setShowTransactions(!showTransactions)}
              className="w-full p-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Receipt className="w-5 h-5" style={{ color: '#2A9FCB' }} />
                <div className="text-left">
                  <h3 className="font-semibold" style={{ color: '#2A9FCB' }}>Storico Pagamenti</h3>
                  <p className="text-xs text-gray-500">Visualizza tutte le tue transazioni</p>
                </div>
              </div>
              {showTransactions ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {showTransactions && (
              <div className="border-t" style={{ borderColor: '#E5E7EB' }}>
                {/* Year Selector & Annual Summary */}
                <div className="p-4 flex flex-wrap items-center justify-between gap-3" style={{ background: '#F9FAFB' }}>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: '#2A9FCB' }} />
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      className="px-3 py-1.5 rounded-lg border text-sm font-medium"
                      style={{ borderColor: '#E5E7EB', color: '#2A9FCB' }}
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={downloadAnnualSummary}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90"
                    style={{ background: '#2A9FCB' }}
                  >
                    <FileText className="w-4 h-4" />
                    Riepilogo Annuale {selectedYear}
                  </button>
                </div>

                {/* Stats */}
                <div className="px-4 py-3 flex justify-between items-center text-sm" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
                  <span className="text-gray-600">
                    <strong>{transactionStats.count}</strong> transazioni nel {selectedYear}
                  </span>
                  <span className="font-bold" style={{ color: '#2A9FCB' }}>
                    Totale: € {transactionStats.total.toFixed(2)}
                  </span>
                </div>

                {/* Transaction List */}
                <div className="p-4">
                  {transactionsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#2A9FCB' }} />
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="text-center py-8">
                      <Receipt className="w-10 h-10 mx-auto mb-3" style={{ color: '#D1D5DB' }} />
                      <p className="text-gray-500">Nessuna transazione nel {selectedYear}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map((transaction, idx) => (
                        <div
                          key={transaction.id || idx}
                          className="flex items-center justify-between p-3 rounded-lg"
                          style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                transaction.type === 'subscription' 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-sky-100 text-sky-700'
                              }`}>
                                {transaction.type === 'subscription' ? 'Abbonamento' : 'Prestazione'}
                              </span>
                              <span className="text-xs text-gray-400">
                                {transaction.date ? new Date(transaction.date).toLocaleDateString('it-IT') : '-'}
                              </span>
                            </div>
                            <p className="font-medium text-sm mt-1 truncate" style={{ color: '#374151' }}>
                              {transaction.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 ml-3">
                            <span className="font-bold whitespace-nowrap" style={{ color: '#2A9FCB' }}>
                              € {(transaction.amount || 0).toFixed(2)}
                            </span>
                            {transaction.invoice_available && (
                              <button
                                onClick={() => downloadTransactionPDF(transaction.id)}
                                className="p-2 rounded-lg hover:bg-white transition-colors"
                                title="Scarica ricevuta"
                              >
                                <Download className="w-4 h-4" style={{ color: '#2A9FCB' }} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Note fiscale */}
                <div className="px-4 pb-4">
                  <div className="p-3 rounded-lg text-xs" style={{ background: '#FEF3C7', color: '#92400E' }}>
                    <strong>📋 Per la dichiarazione dei redditi:</strong> Le spese sanitarie sono detraibili al 19%. 
                    Scarica il riepilogo annuale per avere un documento completo delle tue spese.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Upgrade CTA for non-premium users */}
          {design.type !== 'PREMIUM' && (
            <div className="p-5 rounded-xl text-center"
                 style={{ background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)', border: '1px solid #FCD34D' }}>
              <Crown className="w-8 h-8 mx-auto mb-2" style={{ color: '#92400E' }} />
              <h4 className="font-semibold mb-1" style={{ color: '#92400E' }}>Passa a Premium</h4>
              <p className="text-sm mb-3" style={{ color: '#A16207' }}>
                Accesso prioritario, sconti esclusivi e servizi dedicati
              </p>
              <button
                onClick={() => navigate('/checkout?plan=premium')}
                className="px-6 py-2 rounded-lg font-semibold text-white transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
              >
                Passa a Premium
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientCard;
